import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Icon, Typography } from '@mui/material';

import { borderRadius, palette, spaces } from '../../assets/styles/themes/tokens';
import getMuiIcon, { IconName } from '../../utils/getMuiIcon';
import { UserPaymentMethodType } from '../../types/User';
import PaymentMethodsView from './PaymentMethodsView';
import PaymentMethodsForm from './PaymentMethodsForm';

interface PaymentMethodsProps {
  isEditMode?: boolean;
  availablePaymentMethods: UserPaymentMethodType[];
  selectedMethodType: string;
  currentMethodIcon: IconName;
  currentMethodText: string;
  onChangeMethod: (method: UserPaymentMethodType) => void;
}

const PaymentMethods = ({
  isEditMode,
  availablePaymentMethods,
  selectedMethodType,
  currentMethodIcon,
  currentMethodText,
  onChangeMethod
}: PaymentMethodsProps) => {
  const { t } = useTranslation();
  const [isPaymentMethodUpdated, setIsPaymentMethodUpdated] = useState<boolean>(false);
  const prevIsEditModeRef = useRef(isEditMode);

  useEffect(() => {
    if (prevIsEditModeRef.current && !isEditMode) {
      setIsPaymentMethodUpdated(false);
    }
    prevIsEditModeRef.current = isEditMode;
  }, [isEditMode]);

  return (
    (isEditMode && !isPaymentMethodUpdated) ?
      <Box>
        <Box sx={{ marginBottom: spaces.lg }}>
          {availablePaymentMethods.map((method, index) => (
            <Button
              data-testid={`${method}-${index}`}
              key={index}
              size='large'
              sx={{
                height: '46px',
                borderRadius: borderRadius.xs,
                marginRight: spaces.xs,
                marginBottom: spaces.xs,
                borderColor: selectedMethodType === method ? palette.primary.main : palette.elevation.outlined,
                '& svg': { width: 'auto', height: 'auto' }
              }}
              variant='outlined'
              onClick={() => onChangeMethod(method)}
            >
              <Icon
                component={getMuiIcon(method as IconName)}
              />
              {method === UserPaymentMethodType.INVOICE &&
                <Typography sx={{ marginLeft: spaces.xxs, fontSize: '12px' }}>{t('Invoice')}</Typography>}
            </Button>
          ))}
        </Box>
        <PaymentMethodsForm
          selectedMethodType={selectedMethodType}
          onPaymentMethodUpdated={() => setIsPaymentMethodUpdated(true)}
        />
      </Box> :
      <PaymentMethodsView
        currentMethodIcon={currentMethodIcon}
        currentMethodText={currentMethodText}
      />
  );
};

export default PaymentMethods;
